"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { Flame, Skull, Crown, ArrowUpRight } from "lucide-react";
import CdnImage from "@/components/CdnImage";
import UserHoverCard from "@/app/components/UserHoverCard";
import GradientAvatar from "@/app/components/GradientAvatar";
import { useT } from "@/hooks/useT";
import { fmtCents } from "@/lib/format-price";
import CopyPauseButton from "../shared/CopyPauseButton";
import { useMarketData } from "@/hooks/useMarketData";
import { formatUSD, timeAgo, displayTraderName, abbreviateOutcomeLabel } from "../../helpers/format";
import { CARD_BASE } from "../../helpers/styles";
import type { ActivityEvent } from "../../types";

// Copy Trade Card — same slim event-card template as WhaleTradeRow,
// adapted for two-actor copy events: "copier copied @leader".
export default function CopyTradeRow({
  event,
  onCopy,
  onVote,
  followedTraders,
  onToggleCopy,
}: {
  event: ActivityEvent;
  onCopy: (event: ActivityEvent) => void;
  onVote: (eventId: string, voteType: number, sourceTable: string) => void;
  followedTraders: Map<string, { leaderId: string; enabled: boolean; label?: string | null }>;
  onToggleCopy: (wallet: string) => void;
}) {
  const t = useT();
  const router = useRouter();

  const isBuy = event.side !== "SELL";
  const isYes = event.outcome?.toLowerCase().includes('yes') || event.outcome?.toLowerCase().includes('up');
  const copierName = displayTraderName(event.metadata?.copier_username || "Trader");
  // The viewer's private tag for this leader wins over the real handle.
  const leaderTag = followedTraders.get((event.metadata?.leader_address || "").toLowerCase())?.label;
  const leaderName = leaderTag?.trim() || displayTraderName(event.metadata?.leader_username || event.metadata?.leader_address || "trader");

  const marketTitle = event.market_title;
  const marketHref = event.event_slug
    ? `/event/${event.event_slug}`
    : event.market_slug
      ? `/market/${event.market_slug}`
      : null;

  const leaderHref = event.metadata?.leader_address
    ? `/traders/@${event.metadata.leader_address}`
    : `/profile/${event.metadata?.leader_username || 'trader'}`;
  const copierHref = `/profile/${event.metadata?.copier_username || 'trader'}`;

  let buttonOutcomes: string[] = [];
  let buttonPrices: number[] = [];
  let buttonLabels: string[] = [];
  if (event.price != null && event.outcome) {
    const rawP = Number(event.price);
    const tradedProb = rawP > 1 ? rawP / 100 : rawP;
    if (tradedProb >= 0 && tradedProb <= 1) {
      const outcomeLower = event.outcome.toLowerCase().trim();
      const titleHasVs = !!marketTitle && / vs\.? /i.test(marketTitle);
      if (titleHasVs) {
        const sides = marketTitle!.split(/ vs\.? /i).map(s => s.trim().replace(/\?$/, ""));
        if (sides.length === 2) {
          const idx = sides.findIndex(s => s.toLowerCase() === outcomeLower);
          if (idx >= 0) {
            buttonOutcomes = sides;
            buttonPrices = idx === 0 ? [tradedProb, 1 - tradedProb] : [1 - tradedProb, tradedProb];
          }
        }
      }
      if (buttonOutcomes.length === 0 && (outcomeLower === "yes" || outcomeLower === "no")) {
        buttonOutcomes = ["Yes", "No"];
        buttonPrices = outcomeLower === "yes" ? [tradedProb, 1 - tradedProb] : [1 - tradedProb, tradedProb];
      }
    }
  }
  buttonLabels = buttonOutcomes.slice();
  if (buttonOutcomes.length === 2 && event.market_slug && / vs\.? /i.test(marketTitle || '')) {
    const parts = event.market_slug.split("-");
    const leaguePrefixes = new Set(["nba", "nfl", "mlb", "nhl", "epl", "laliga", "seriea", "ucl", "ncaaf", "ncaab", "f1", "atp", "wta", "mls", "mma", "ufc"]);
    const tokens: string[] = [];
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i];
      if (i === 0 && leaguePrefixes.has(p)) continue;
      if (/^\d{2,4}$/.test(p)) break;
      if (p.length >= 2 && p.length <= 4 && /^[a-z]+$/i.test(p)) tokens.push(p.toUpperCase());
    }
    if (tokens.length >= 2) buttonLabels = [tokens[0], tokens[1]];
  }
  buttonLabels = buttonLabels.map(abbreviateOutcomeLabel);

  const { data: liveMarketData } = useMarketData(event.market_slug, {
    enabled: !!event.market_slug,
    source: 'polymarket',
  });
  const marketVolume = liveMarketData?.volume ?? null;
  const marketLiquidity = liveMarketData?.liquidity ?? null;
  const marketEndDate = liveMarketData?.endDate ?? null;
  const marketClosed = liveMarketData?.closed === true;

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
      return;
    }
    router.push(`/activity/${event.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      onClick={handleCardClick}
      className={CARD_BASE}
    >
      <div className="py-2.5 px-1">
        <div className="flex items-center gap-2.5">
          <UserHoverCard username={event.metadata?.copier_username || undefined}>
            <Link href={copierHref} prefetch={false} className="flex-shrink-0">
              {event.metadata?.copier_image ? (
                <CdnImage src={event.metadata.copier_image} alt={copierName} width={32} height={32} sizes="32px" className="w-8 h-8 rounded-[5px] object-cover" />
              ) : (
                <GradientAvatar seed={copierName} size={32} />
              )}
            </Link>
          </UserHoverCard>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-3">
              <div className="flex items-baseline gap-1.5 flex-wrap min-w-0">
                <UserHoverCard username={event.metadata?.copier_username || undefined}>
                  <Link href={copierHref} prefetch={false} className="text-sm font-bold text-gray-900 dark:text-white hover:underline truncate">
                    {copierName}
                  </Link>
                </UserHoverCard>
                <span className="text-xs text-gray-500 dark:text-gray-400">{t("activity.action.copied")}</span>
                <UserHoverCard username={event.metadata?.leader_username || undefined} walletAddress={event.metadata?.leader_address || undefined}>
                  <Link href={leaderHref} prefetch={false} className="inline-flex items-center gap-1 text-sm font-bold text-emerald-600 dark:text-emerald-400 hover:underline">
                    <Crown className="w-3 h-3 text-amber-500" />
                    @{leaderName}
                  </Link>
                </UserHoverCard>
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-[5px] text-[11px] font-semibold ${
                  isBuy
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                    : 'bg-red-500/10 text-red-600 dark:text-red-400'
                }`}>
                  {isBuy ? t("activity.action.bought") : t("activity.action.sold")}
                </span>
                {event.size_usd != null && (
                  <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                    {formatUSD(event.size_usd)}
                  </span>
                )}
                {event.outcome && (
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-[5px] text-[11px] font-semibold ${
                    isYes
                      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                      : 'bg-red-500/10 text-red-600 dark:text-red-400'
                  }`}>
                    {event.outcome}
                  </span>
                )}
                {event.price != null && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    @ <span className="font-semibold text-gray-900 dark:text-white">{fmtCents(Number(event.price))}¢</span>
                  </span>
                )}
              </div>
              <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-[5px] bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.06] text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {timeAgo(event.event_at, t)}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-1 text-[10px]">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[5px] border bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400 font-semibold whitespace-nowrap">
                <Crown className="w-2.5 h-2.5" />
                {t("activity.badge.copy")}
              </span>
              {event.metadata?.leader_size_usd != null && (
                <span className="text-gray-500 dark:text-gray-400">
                  leader: <span className="font-semibold text-gray-700 dark:text-gray-300">{formatUSD(event.metadata.leader_size_usd)}</span>
                </span>
              )}
              {event.metadata?.grouped_trade_count > 1 && (
                <span className="text-gray-400">{event.metadata.grouped_trade_count} trades</span>
              )}
            </div>
          </div>
        </div>

        {marketHref && marketTitle && (
          <Link
            href={marketHref}
            prefetch={false}
            onClick={(e) => e.stopPropagation()}
            className="block mt-2.5 p-2.5 rounded-[5px] bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:border-gray-300 dark:hover:border-white/[0.1] transition-colors"
          >
            <div className="flex items-center gap-2.5">
              {event.market_image && (
                <div className="w-8 h-8 rounded-[5px] overflow-hidden flex-shrink-0 bg-gray-200 dark:bg-white/[0.06]">
                  <CdnImage src={event.market_image} alt={marketTitle} width={32} height={32} sizes="32px" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {marketTitle}
                </h3>
                {(marketVolume != null || marketLiquidity != null || marketEndDate) && (() => {
                  const src = (event.metadata?.source as string) || 'polymarket';
                  const sourceLogo =
                    src === 'kalshi' ? '/logos/kalshi/k-icon.svg'
                    : src === 'hyperliquid' ? '/logos/hyperliquid/symbol-mint.svg'
                    : src === 'predict' ? ''
                    : '/logos/polymarket/icon-white.png';
                  const sourceLetter = src === 'predict' ? 'P' : '';
                  const sourceBg =
                    src === 'kalshi' ? 'bg-emerald-500/15'
                    : src === 'predict' ? 'bg-violet-500/15'
                    : src === 'hyperliquid' ? 'bg-cyan-500/15'
                    : 'bg-blue-500/15';
                  return (
                  <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-gray-500 dark:text-gray-400 flex-wrap">
                    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-[4px] flex-shrink-0 ${sourceBg}`}>
                      {sourceLogo ? (
                        <img src={sourceLogo} alt={src} className="w-3.5 h-3.5 object-contain" />
                      ) : (
                        <span className="text-[10px] font-bold text-violet-600 dark:text-violet-400">{sourceLetter}</span>
                      )}
                    </span>
                    {marketClosed && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-[5px] bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 font-semibold whitespace-nowrap">
                        {t("activity.badge.marketClosed")}
                      </span>
                    )}
                    {marketVolume != null && marketVolume > 0 && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="whitespace-nowrap tabular-nums">{formatUSD(marketVolume)} vol</span>
                      </>
                    )}
                    {marketLiquidity != null && marketLiquidity > 0 && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="whitespace-nowrap tabular-nums">{formatUSD(marketLiquidity)} liq</span>
                      </>
                    )}
                    {marketEndDate && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="whitespace-nowrap">
                          {new Date(marketEndDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </span>
                      </>
                    )}
                  </div>
                  );
                })()}
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
            </div>
            {buttonOutcomes.length === 2 && (
              <div className="flex gap-1.5 mt-2">
                {[0, 1].map((i) => {
                  const isEmerald = i === 0;
                  const borderColor = isEmerald ? 'border-emerald-500/30 hover:border-emerald-500/60' : 'border-red-500/30 hover:border-red-500/60';
                  const bgColor = isEmerald ? 'bg-emerald-500/5 hover:bg-emerald-500/10' : 'bg-red-500/5 hover:bg-red-500/10';
                  const textColor = isEmerald ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
                  return (
                    <Link
                      key={buttonOutcomes[i]}
                      href={`${marketHref}?outcome=${encodeURIComponent(buttonOutcomes[i].toLowerCase())}`}
                      prefetch={false}
                      onClick={(e) => e.stopPropagation()}
                      className={`flex-1 flex items-center justify-between px-2.5 py-1.5 rounded-[5px] border transition-colors ${borderColor} ${bgColor}`}
                    >
                      <span className={`text-[10px] font-bold uppercase tracking-wide truncate ${textColor}`}>
                        {buttonLabels[i]}
                      </span>
                      <span className="text-xs font-bold text-gray-900 dark:text-white tabular-nums flex-shrink-0 ml-2">
                        {(buttonPrices[i] * 100).toFixed(1)}¢
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </Link>
        )}

        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => onVote(event.id, 1, 'activity_event')}
              className={`flex items-center gap-1 px-1.5 py-1 rounded-[5px] text-xs transition-colors ${
                event.user_vote === 1 ? 'text-orange-500 bg-orange-500/10' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-orange-500'
              }`}
            >
              <Flame className="w-3.5 h-3.5" />
              <span>{event.reactions?.fire || event.likes || 0}</span>
            </button>
            <button
              onClick={() => onVote(event.id, -1, 'activity_event')}
              className={`flex items-center gap-1 px-1.5 py-1 rounded-[5px] text-xs transition-colors ${
                event.user_vote === -1 ? 'text-red-500 bg-red-500/10' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-red-500'
              }`}
            >
              <Skull className="w-3.5 h-3.5" />
              <span>{event.reactions?.skull || event.dislikes || 0}</span>
            </button>
          </div>
          <CopyPauseButton
            wallet={event.metadata?.leader_address || ''}
            username={leaderName}
            avatar={event.metadata?.leader_image}
            followedTraders={followedTraders}
            onToggleCopy={onToggleCopy}
            onCopy={() => onCopy(event)}
          />
        </div>
      </div>
    </motion.div>
  );
}
